import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Table, Input, Switch, Button, Typography, Flex } from "antd"
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons"
import { useWatch } from "antd/es/form/Form"
import { useDispatch, useSelector } from "react-redux"
import styles from "./AssetListDetails.module.css"
import {
  checkAssetDeactivation,
  deactivateAsset,
  activateAsset,
  checkAssetDeletion,
  deleteAsset,
  selectAssetDeactivationCheck,
  selectAssetDeactivationCheckLoading,
  selectDeactivateAssetLoading,
  selectActivateAssetLoading,
  selectAssetDeletionCheck,
  selectAssetDeletionCheckLoading,
  selectDeleteAssetLoading
} from "store/boats"
import DeleteDeactivateModal from "../../../../pages/guide/resources/equipment/modal/DeactivateDelete"
import NoExperiencesDeleteDeactivateModal from "../../../../pages/guide/resources/equipment/modal/NoexperiencesDeleteDeactivate"
import { determineDeactivateMode } from "../../../../pages/guide/resources/equipment/utils/determineDeactivateMode"
import { EQUIPMENT_DELETE_DEACTIVATE_MODES } from "../../../../pages/guide/resources/equipment/constants"
import { toaster } from "components/common/Toast"

const { Text } = Typography

const AssetListDetails = ({ form, categoryName, assets, setAssets, equipmentId }) => {
  const dispatch = useDispatch()
  const equipmentTitle = useWatch("name", form)
  const [tableData, setTableData] = useState([])
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [targetAsset, setTargetAsset] = useState(null)
  const [activatingAssetId, setActivatingAssetId] = useState(null)

  const deactivationCheckData = useSelector(selectAssetDeactivationCheck)
  const isCheckingDeactivation = useSelector(selectAssetDeactivationCheckLoading)
  const isDeactivating = useSelector(selectDeactivateAssetLoading)
  const isActivating = useSelector(selectActivateAssetLoading)

  const deletionCheckData = useSelector(selectAssetDeletionCheck)
  const isCheckingDeletion = useSelector(selectAssetDeletionCheckLoading)
  const isDeleting = useSelector(selectDeleteAssetLoading)

  useEffect(() => {
    setAssets((prev) =>
      prev.map((item) => ({
        ...item,
        title: equipmentTitle ?? "",
        category: categoryName ?? ""
      }))
    )
  }, [equipmentTitle, categoryName])

  const handleAddRow = () => {
    const activeAssets = assets.filter((a) => !a._destroy && !a.isPlaceholder)
    const newKey = activeAssets.length ? activeAssets.at(-1).key + 1 : 1

    // Auto-generate asset number
    const existingNumbers = activeAssets
      .map((a) => {
        const num = Number.parseInt(a.assetNumber, 10)
        return Number.isNaN(num) ? 0 : num
      })
      .filter((n) => n > 0)

    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0
    const nextNumber = maxNumber + 1
    const autoAssetNumber = String(nextNumber).padStart(4, "0")

    setAssets((prev) => [
      ...prev,
      {
        key: newKey,
        title: (equipmentTitle ?? form.getFieldValue("name")) || "",
        category: categoryName || "",
        assetNumber: autoAssetNumber,
        available: true,
        destroy: false
      }
    ])
  }

  const handleChange = (value, key, field) => {
    setAssets((prev) => prev.map((item) => (item.key === key ? { ...item, [field]: value } : item)))
  }

  const handleSwitchChange = (checked, record) => {
    // If it's a local unsaved asset (no ID), just update state
    if (!record.id) {
      handleChange(checked, record.key, "available")
      return
    }

    // If turning ON a saved asset, trigger activation API
    if (checked) {
      setActivatingAssetId(record.id)
      dispatch(activateAsset(record.id))
        .then((response) => {
          // Update state on success
          handleChange(true, record.key, "available")
          const successMessage = response?.message || `Asset #${record.assetNumber} has been activated successfully`
          toaster.success(successMessage)
          setActivatingAssetId(null)
        })
        .catch((error) => {
          console.error("Failed to activate asset", error)
          toaster.error("Failed to activate asset")
          setActivatingAssetId(null)
        })
      return
    }

    // If turning OFF a saved asset, trigger deactivation flow
    setTargetAsset(record)
    dispatch(checkAssetDeactivation(record.id))
      .then(() => {
        // Always open modal after check returns (assuming success or safe failure handling)
        // The modal content depends on the selector data which updates via Redux
        setDeactivateModalVisible(true)
      })
      .catch((error) => {
        console.error("Failed to check deactivation status", error)
      })
  }

  const handleDeactivateConfirm = (data) => {
    if (!targetAsset) return

    dispatch(deactivateAsset(targetAsset.id, data))
      .then((response) => {
        // On success, update the local state to unavailable
        handleChange(false, targetAsset.key, "available")
        setDeactivateModalVisible(false)
        setTargetAsset(null)
        const successMessage = response?.message || `Asset #${targetAsset.assetNumber} has been deactivated successfully`
        toaster.success(successMessage)
      })
      .catch((error) => {
        console.error("Failed to deactivate asset", error)
        toaster.error("Failed to deactivate asset")
      })
  }

  const handleDeactivateCancel = () => {
    setDeactivateModalVisible(false)
    setTargetAsset(null)
  }

  // When deleting we keep persisted rows (with id) and mark them _destroy=true so backend can remove them.
  // For newly added (no id yet) rows we can simply discard them.
  const handleDeleteRow = (key) => {
    const asset = assets.find((a) => a.key === key)

    // If it's an unsaved asset (no ID), just remove it locally
    if (!asset?.id) {
      setAssets((prev) =>
        prev
          .map((item) => {
            if (item.key !== key) return item
            // Unsaved row: remove completely
            return null
          })
          .filter(Boolean)
      )
      return
    }

    // If it's a saved asset, trigger deletion flow with API call
    setTargetAsset(asset)
    dispatch(checkAssetDeletion(asset.id))
      .then(() => {
        // Always open modal after check returns
        // The modal content depends on the selector data which updates via Redux
        setDeleteModalVisible(true)
      })
      .catch((error) => {
        console.error("Failed to check deletion status", error)
      })
  }

  const handleDeleteConfirm = (data) => {
    if (!targetAsset) return

    // Use equipmentId prop instead of form field
    if (!equipmentId) {
      toaster.error("Equipment ID not found")
      return
    }

    dispatch(deleteAsset(equipmentId, targetAsset.id, data))
      .then((response) => {
        // On success, mark the asset as destroyed locally
        setAssets((prev) =>
          prev.map((item) =>
            item.key === targetAsset.key ? { id: item.id, _destroy: true, key: item.key } : item
          )
        )
        setDeleteModalVisible(false)
        setTargetAsset(null)
        const successMessage = response?.message || `Asset #${targetAsset.assetNumber} has been deleted successfully`
        toaster.success(successMessage)
      })
      .catch((error) => {
        console.error("Failed to delete asset", error)
        toaster.error("Failed to delete asset")
      })
  }

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false)
    setTargetAsset(null)
  }

  useEffect(() => {
    const filteredAssets = assets.filter((a) => !a._destroy)
    if (filteredAssets.length > 0) {
      setTableData(filteredAssets)
    } else if (equipmentTitle && categoryName) {
      // Automatically create first asset when both category and title are filled
      const firstAsset = {
        key: 1,
        title: equipmentTitle,
        category: categoryName,
        assetNumber: "0001",
        available: true,
        isPlaceholder: false
      }
      setAssets([firstAsset])
      setTableData([firstAsset])
    } else {
      // Show placeholder when category or title is missing
      setTableData([
        {
          key: "placeholder",
          title: equipmentTitle || "Equipment Title",
          category: categoryName || "Category",
          assetNumber: "0000",
          available: false,
          isPlaceholder: true
        }
      ])
    }
  }, [assets, equipmentTitle, categoryName, setAssets])

  const columns = [
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      render: (_text, _record, idx) => <span>{idx + 1}</span>,
      width: 40
    },
    {
      title: "Equipment Title",
      dataIndex: "title",
      key: "title",
      render: (text) => <span>{text || "-"}</span>
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (text) => <span>{text || "-"}</span>,
      width: 160
    },
    {
      title: "Asset Number",
      dataIndex: "assetNumber",
      key: "assetNumber",
      render: (text, record) => (
        <Input
          value={text}
          placeholder="Asset Number"
          disabled={record.isPlaceholder || !record.available}
          onChange={(e) => handleChange(e.target.value, record.key, "assetNumber")}
        />
      ),
      width: 140
    },
    {
      title: "Available",
      dataIndex: "available",
      key: "available",
      render: (val, record) => (
        <Switch
          checked={val}
          disabled={record.isPlaceholder}
          loading={
            (isCheckingDeactivation && targetAsset?.key === record.key) ||
            (isActivating && activatingAssetId === record.id)
          }
          onChange={(checked) => handleSwitchChange(checked, record)}
        />
      ),
      width: 100
    },
    {
      title: "",
      key: "action",
      align: "center",
      render: (_, record) => {
        if (record.isPlaceholder) {
          return null
        }

        const activeAssetsCount = assets.filter((a) => !a._destroy && !a.isPlaceholder).length
        const isOnlyAsset = activeAssetsCount === 1

        return (
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteRow(record.key)}
            aria-label="Delete row"
            className={styles.deleteButton}
            disabled={!record.available || isOnlyAsset}
            loading={isCheckingDeletion && targetAsset?.key === record.key}
          />
        )
      },
      width: 50
    }
  ]

  // Prepare data for the deactivate modal
  const mode = determineDeactivateMode(deactivationCheckData)

  // Extract separate messages for experiences and bookings
  const expMessage = deactivationCheckData?.exp_message || null
  const bookingMessage = deactivationCheckData?.booking_message || null

  const assignedExperiencesList = deactivationCheckData?.assigned_trips || []

  const assignedExperiences = deactivationCheckData?.assigned_trips?.map((trip) => ({
    name: trip.trip_name,
    trip_uuid: trip.trip_uuid,
    trip_id: trip.trip_id,
    status: trip.status,
    link: trip.trip_uuid ? `/pro/experiences/edit/${trip.trip_uuid}` : `/pro/experiences/${trip.trip_id}`
  }))

  const upcomingBookings = deactivationCheckData?.active_bookings?.map((booking) => ({
    experienceName: booking.experience_name,
    booked_date: booking.booked_date, // Use booked_date field as expected by the modal component
    start_time: booking.start_time,
    end_time: booking.end_time,
    id: String(booking.booking_id),
    booking_id: booking.booking_id,
    booking_uuid: booking.booking_uuid,
    clientName: booking.client_name,
    client_name: booking.client_name,
    replaceWith: null, // Initial value
    canSubstitute: booking.can_substitute,
    available_substitutes: booking.available_substitutes // Pass raw data, let FullySubstitutable component handle transformation
  }))

  return (
    <div className={styles.assetListDetailsContainer}>
      <Flex justify="space-between" align="center" className={`mb-s ${styles.assetsHeader}`}>
        <Text strong className={styles.assetListTitle}>
          Assets{" "}
          <Text fw="500" className={styles.AssetListDetails}>
            ({assets.filter((a) => !a._destroy && !a.isPlaceholder).length})
          </Text>
        </Text>

        <Button color="primary" variant="solid" icon={<PlusOutlined />} onClick={handleAddRow} />
      </Flex>
      <Table
        dataSource={tableData}
        columns={columns}
        pagination={false}
        rowKey="key"
        className="asset-list-table"
        rowClassName={(record) => (record.isPlaceholder || !record.available ? styles.assetRowDisabled : "")}
      />

      <Button type="link" onClick={handleAddRow} className="mt-s p-0">
        + Add New Item
      </Button>

      {deactivateModalVisible && mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES && (
        <NoExperiencesDeleteDeactivateModal
          open={deactivateModalVisible}
          title="Deactivate Asset?"
          description="You are going to deactivate "
          boldText={`Asset #${targetAsset?.assetNumber || ""} (${targetAsset?.title || ""})`}
          afterBoldText=". They will no longer be available until reactivated."
          onCancel={handleDeactivateCancel}
          onOk={handleDeactivateConfirm}
          okText="Deactivate"
          cancelText="Cancel"
          loading={isDeactivating}
        />
      )}

      {deactivateModalVisible && mode !== EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES && (
        <DeleteDeactivateModal
          visible={deactivateModalVisible}
          onCancel={handleDeactivateCancel}
          onOk={handleDeactivateConfirm}
          title="Deactivate Asset?"
          okText="Deactivate"
          cancelText="Cancel"
          loading={isDeactivating}
          mode={mode}
          list={assignedExperiencesList}
          upcomingBookings={upcomingBookings}
          assignedExperiences={assignedExperiences}
          warning={
            deactivationCheckData?.assigned_trips?.length > 0
              ? `By deactivating Asset #${targetAsset?.assetNumber || ""} (${targetAsset?.title || ""}), it will be removed from all assigned experiences. This could impact experience availability.`
              : null
          }
          subtitle={(() => {
            const assetNumber = targetAsset?.assetNumber || ""
            const assetTitle = targetAsset?.title || ""
            if (deactivationCheckData?.active_bookings?.length > 0) {
              return (
                <>
                  <strong>Asset #{assetNumber} ({assetTitle})</strong> is assigned to {deactivationCheckData.active_bookings.length} upcoming trips. To deactivate <strong>Asset #{assetNumber} ({assetTitle})</strong>, you must resolve these bookings.
                </>
              )
            }
            if (deactivationCheckData?.assigned_trips?.length > 0) {
              return (
                <>
                  <strong>Asset #{assetNumber} ({assetTitle})</strong> is assigned to {deactivationCheckData.assigned_trips.length} experiences.
                </>
              )
            }
            return (
              <>
                You are going to deactivate <strong>Asset #{assetNumber} ({assetTitle})</strong>. They will no longer be available until reactivated.
              </>
            )
          })()}
          expMessage={expMessage}
          bookingMessage={bookingMessage}
        />
      )}

      {deleteModalVisible && determineDeactivateMode(deletionCheckData) === EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES && (
        <NoExperiencesDeleteDeactivateModal
          open={deleteModalVisible}
          title="Delete Asset?"
          description="You are going to permanently delete "
          boldText={`Asset #${targetAsset?.assetNumber || ""} (${targetAsset?.title || ""})`}
          afterBoldText=". This action cannot be undone."
          onCancel={handleDeleteCancel}
          onOk={handleDeleteConfirm}
          okText="Delete"
          cancelText="Cancel"
          loading={isDeleting}
        />
      )}

      {deleteModalVisible && determineDeactivateMode(deletionCheckData) !== EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES && (
        <DeleteDeactivateModal
          visible={deleteModalVisible}
          onCancel={handleDeleteCancel}
          onOk={handleDeleteConfirm}
          title="Delete Asset?"
          okText="Delete"
          cancelText="Cancel"
          loading={isDeleting}
          mode={determineDeactivateMode(deletionCheckData)}
          list={deletionCheckData?.assigned_trips || []}
          upcomingBookings={deletionCheckData?.active_bookings?.map((booking) => ({
            experienceName: booking.experience_name,
            booked_date: booking.booked_date, // Use booked_date field as expected by the modal component
            start_time: booking.start_time,
            end_time: booking.end_time,
            id: String(booking.booking_id),
            booking_id: booking.booking_id,
            booking_uuid: booking.booking_uuid,
            clientName: booking.client_name,
            client_name: booking.client_name,
            replaceWith: null,
            canSubstitute: booking.can_substitute,
            available_substitutes: booking.available_substitutes
          }))}
          assignedExperiences={deletionCheckData?.assigned_trips?.map((trip) => ({
            name: trip.trip_name,
            trip_uuid: trip.trip_uuid,
            trip_id: trip.trip_id,
            status: trip.status,
            link: trip.trip_uuid ? `/pro/experiences/edit/${trip.trip_uuid}` : `/pro/experiences/${trip.trip_id}`
          }))}
          warning={
            deletionCheckData?.assigned_trips?.length > 0
              ? `By deleting Asset #${targetAsset?.assetNumber || ""} (${targetAsset?.title || ""}), it will be permanently removed from all assigned experiences. This action cannot be undone.`
              : null
          }
          subtitle={(() => {
            const assetNumber = targetAsset?.assetNumber || ""
            const assetTitle = targetAsset?.title || ""
            if (deletionCheckData?.active_bookings?.length > 0) {
              return (
                <>
                  <strong>Asset #{assetNumber} ({assetTitle})</strong> is assigned to {deletionCheckData.active_bookings.length} upcoming trips. To delete <strong>Asset #{assetNumber} ({assetTitle})</strong>, you must resolve these bookings.
                </>
              )
            }
            if (deletionCheckData?.assigned_trips?.length > 0) {
              return (
                <>
                  <strong>Asset #{assetNumber} ({assetTitle})</strong> is assigned to {deletionCheckData.assigned_trips.length} experiences.
                </>
              )
            }
            return (
              <>
                You are going to permanently delete <strong>Asset #{assetNumber} ({assetTitle})</strong>. This action cannot be undone.
              </>
            )
          })()}
          expMessage={deletionCheckData?.exp_message || null}
          bookingMessage={deletionCheckData?.booking_message || null}
        />
      )}
    </div>
  )
}

export default AssetListDetails

AssetListDetails.propTypes = {
  form: PropTypes.object.isRequired,
  categoryName: PropTypes.string,
  assets: PropTypes.array.isRequired,
  setAssets: PropTypes.func.isRequired,
  equipmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}
